import type { ComponentType, SVGProps } from 'react';
import { RefreshCw } from 'lucide-react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import SyncedCheckmark from './SyncedCheckmark';

interface SyncStatusChipProps {
  // Badge gating is fully decided by the caller (shouldShowUnsyncedBadge,
  // channel-aware — see unsyncedBadge.ts): it can be true from the PMS side
  // alone (e.g. a persisted photoset.hasUnsyncedPmsChanges with no local
  // session-dirty state), so this component must trust it rather than
  // re-deriving its own condition.
  showBadge: boolean;
  // Deliverable session-local dirty count (visibleDirtyCount — only edits the
  // lit channel can actually deliver). Counted copy returned in QA round 4
  // (2026-08-19): "3 Unsynced Changes" at lg+, a numeric badge on the sub-lg
  // circle. Honest-zero rule: the badge can be lit purely by the backend's
  // persisted booleans (edits from a previous session), where no count
  // exists — 0 here keeps the uncounted "Unsynced Changes" copy and no
  // numeric badge, never a "0".
  unsyncedCount?: number;
  // "Synced to {pmsSource}" when present, "Synced" when the entity is
  // webhook-only (Product copy, DV-2750).
  pmsSource: string | null;
  // The old "Photos last synced to X on {date}" line — demoted to a hover
  // tooltip so the timestamp survives the shorter Product copy. '' = no
  // sync history yet, tooltip omitted.
  lastSyncedTooltip: string;
  // True while a sync request (PMS sync or webhook push) is in flight. The
  // refresh icon spins for exactly that duration — it is an activity
  // indicator, not a state-change announcement.
  isSyncing?: boolean;
  // Opens the Sync modal. Only wires up the chip as a button in the
  // unsynced state (showBadge, not mid-sync) — synced/syncing stay plain
  // informational spans. Optional so existing renders/tests without a
  // handler keep the non-interactive span.
  onClick?: () => void;
}

type ChipState = {
  // Lucide icon or the custom SyncedCheckmark — anything svg-prop shaped.
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  text: string;
  /** Plain lg+ text treatment (design 2026-08-19: no pill background). */
  textClass: string;
  /** Sub-lg icon color. Bare icon on the header surface — the soft-palette
   *  circle fill was dropped for every state per Steffon (QA round 5
   *  follow-up; he named synced/unsynced, and leaving the transient syncing
   *  state as the lone filled circle would read as a bug). */
  subLgIconClass: string;
  spin?: boolean;
};

/**
 * Persistent sync-status chip, left of the always-enabled Sync
 * button (DV-2750; chip replaces the roll-in callout per the Unit 4
 * responsive control-bar spec §1.1). Three states: syncing (spinning
 * refresh icon while a request is in flight), unsynced (warning chip),
 * and synced (success chip naming the PMS, or plain "Synced" when
 * webhook-only). Informational in the synced/syncing states; in the
 * unsynced state it doubles as a shortcut into the same Sync modal
 * the adjacent button opens (users expect a clickable-looking status to be
 * clickable) when the caller passes `onClick`. The caller hides the whole
 * chip when the property has no delivery channel (shouldShowSyncStateNote)
 * or while the sync-state inputs are still loading (prevents a
 * synced-green flash before the photoset's persisted flags arrive).
 *
 * Rendering (2026-08-19 design update; circle fill dropped 2026-08-20): lg+
 * shows a plain icon+text with no pill background; sub-lg collapses to a
 * bare icon in a 32px box (same icons, colored via the state's text tone)
 * with the status text carried for screen readers only — informational,
 * not interactive, so the 44px tap floor doesn't apply.
 */
const SyncStatusChip = ({
  showBadge,
  unsyncedCount = 0,
  pmsSource,
  lastSyncedTooltip,
  isSyncing = false,
  onClick
}: SyncStatusChipProps) => {
  // In-flight state wins over both rest states: a sync can be started from
  // the synced state too, and "Syncing…" is the honest status either way
  // until the request settles.
  const state: ChipState = isSyncing
    ? {
        Icon: RefreshCw,
        text: 'Syncing…',
        textClass: 'text-muted-foreground',
        subLgIconClass: 'text-muted-foreground',
        spin: true
      }
    : showBadge
      ? {
          // The pre-redesign callout's unsynced icon (static warning
          // RefreshCw), restored per Steffon (QA round 4 follow-up) — the
          // redesign round's AlertTriangle is out. Same glyph as the syncing
          // state, distinguished exactly as the old callout did: syncing
          // spins in muted tones, unsynced sits still in warning tones.
          Icon: RefreshCw,
          text:
            unsyncedCount > 0
              ? `${unsyncedCount} Unsynced ${
                  unsyncedCount === 1 ? 'Change' : 'Changes'
                }`
              : 'Unsynced Changes',
          textClass: 'text-warning-soft-foreground',
          subLgIconClass: 'text-warning-soft-foreground'
        }
      : {
          // The pre-redesign callout's animated draw-in checkmark, restored
          // at BOTH widths per Steffon (QA round 5 follow-up) — the redesign
          // round's static CheckCircle2 is out. Rendered only in this state,
          // so flipping into synced (page load, or a sync completing)
          // remounts it and the draw-in replays; reduced-motion rests it at
          // the settled mark (app.css).
          Icon: SyncedCheckmark,
          text: pmsSource ? `Synced to ${pmsSource}` : 'Synced',
          textClass: 'text-success',
          subLgIconClass: 'text-success'
        };

  const { Icon } = state;
  const iconClass = state.spin ? 'motion-safe:animate-spin' : undefined;
  // Clickable only for the settled unsynced state — mid-sync there's
  // nothing to trigger, and a caller that doesn't pass onClick keeps the
  // plain informational span (e.g. existing tests).
  const isClickableUnsynced = !isSyncing && showBadge && Boolean(onClick);
  const chipContent = (
    <>
      {/* lg+: plain icon + text (2026-08-19 design update — no pill background). */}
      <span
        data-testid="sync-status-text"
        className={`hidden lg:inline-flex items-center gap-2 text-sm font-semibold ${state.textClass}`}
      >
        <Icon className={`h-[21px] w-[21px] ${iconClass ?? ''}`} aria-hidden />
        {state.text}
      </span>
      {/* sub-lg: bare icon, same icons as lg+ (Steffon 2026-08-19 — NOT the
          design's alert-circle; the soft circle fill dropped 2026-08-20, QA
          round 5 follow-up). The 32px box stays as spacing + the count
          badge's anchor. Informational, not interactive, so the 44px floor
          doesn't apply; text survives for screen readers (including the
          count when there is one). */}
      <span
        data-testid="sync-status-icon"
        className={`lg:hidden relative inline-flex h-8 w-8 items-center justify-center ${state.subLgIconClass}`}
      >
        <Icon className={`h-[22px] w-[22px] ${iconClass ?? ''}`} aria-hidden />
        {/* Numeric count badge (QA round 4) — only when a deliverable
            session count exists; a badge lit by persisted flags alone shows
            no number (honest-zero rule above). aria-hidden: the sr-only
            status text already carries the counted copy. */}
        {!isSyncing && showBadge && unsyncedCount > 0 && (
          <span
            data-testid="sync-status-count"
            aria-hidden
            className="absolute -right-1.5 -top-1 inline-flex h-[17px] min-w-[17px] items-center justify-center rounded-full border-2 border-card bg-warning px-1 text-[10px] font-bold leading-none text-white"
          >
            {unsyncedCount}
          </span>
        )}
        <span className="sr-only">{state.text}</span>
      </span>
    </>
  );

  const chip = isClickableUnsynced ? (
    <button
      type="button"
      data-testid="sync-status-chip-button"
      onClick={onClick}
      className="inline-flex items-center rounded-sm border-0 bg-transparent p-0 text-left cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      {chipContent}
    </button>
  ) : (
    <span className="inline-flex items-center">{chipContent}</span>
  );

  if (isSyncing || showBadge || !lastSyncedTooltip) return chip;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{chip}</TooltipTrigger>
        <TooltipContent>{lastSyncedTooltip.trim()}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default SyncStatusChip;
